import { isEqual } from 'lodash-es';
import {
  BoxV2,
  FilterType,
  Flex,
  Table,
  TableFilter,
  useFilters,
  useTable,
} from 'portal-commons';
import React, { useEffect, useState } from 'react';
import { Box, Container, Grid } from '@material-ui/core';

import {
  acceptPrimaryDcaCampaign,
  fetchAllDcas,
  getAllUsecaseTypes,
  rejectPrimaryDcaCampaign,
  waiting,
} from '../apis';
import {
  AcceptCampaignModal,
  CampaignsByPrimaryDcasListingRow,
  DcaCampaignDeclineModal,
} from '../components';
import { CampaignActionType } from '../enums';
import { CampaignsByDcaListItem } from '../types';
import CampaignsByPrimaryDcaActionDialog from '../components/CampaignsByPrimaryDcaActionDialog';
import { MAX_EVENT_RECORDS } from '../../events/constants';
import {
  getApprovedMnoOptions,
  getRejectionCategoryOptions,
} from '@/apis/services';
import { CAMPAIGN_REJECTION_ENABLED } from '@/constants';
import { FilterToggleButton, Loader } from '@/shared_elements';
import { SelectItem } from '@/types';
import { UseCaseType } from '@/types/campaign';
import { Dca } from '@/types/user';
import { globalGetService } from '@/utils/globalApiServices';

import '../../../assets/styles/campaigns-by-primaryDcas-module.scss';

const FILTER_CONFIGS = {
  campaignUid: {
    type: FilterType.Text,
    label: 'Campaign ID',
    placeholder: 'Type Campaign ID',
  },
  status: {
    type: FilterType.Dropdown,
    options: [
      { label: 'Active', value: 'ACTIVE' },
      { label: 'Deactivated', value: 'EXPIRED' },
    ],
    label: 'TCR Status',
  },
  secondarySharingStatus: {
    type: FilterType.Dropdown,
    options: [
      { label: 'Accepted', value: 'ACCEPTED' },
      { label: 'Pending', value: 'PENDING' },
    ],
    label: 'Acceptance Status',
    multiple: true,
    width: 260,
  },
  usecase: {
    type: FilterType.Dropdown,
    options: [],
    label: 'Use Case',
  },
  brandUid: {
    type: FilterType.Text,
    label: 'Brand ID',
    placeholder: 'Type Brand ID',
  },
  brandUniversalEin: {
    type: FilterType.Text,
    label: 'Brand Universal EIN',
    placeholder: 'Type Brand Universal EIN',
  },
  brandName: {
    type: FilterType.Text,
    label: 'Brand DBA Name',
    placeholder: 'Type Brand DBA Name',
    suggestions: [],
  },
  primaryDcaUid: {
    type: FilterType.Dropdown,
    options: [],
    label: 'Primary DCA Name',
    multiple: true,
    width: 320,
  },
  provisional: {
    type: FilterType.Dropdown,
    label: 'CNP Migration',
    placeholder: 'Select CNP Migration',
    options: [
      { label: 'True', value: 'true' },
      { label: 'False', value: 'false' },
    ],
  },
};

const headRows = [
  { id: 'campaignUid', label: 'CAMPAIGN ID', sortable: true },
  { id: 'brandUid', label: 'BRAND ID', sortable: true },
  { id: 'brandName', label: 'BRAND DBA NAME', sortable: false },
  { id: 'primaryDcaName', label: 'PRIMARY DCA', sortable: false },
  { id: 'electedMno', label: 'ELECTED FOR MNO', sortable: false },
  { id: 'usecase', label: 'USECASE', sortable: true },
  { id: 'sharedOnDate', label: 'SHARED ON', sortable: true },
  { id: 'status', label: 'TCR STATUS', sortable: false },
  {
    id: 'secondarySharingStatus',
    label: 'ACCEPTANCE STATUS',
    sortable: false,
  },
];

const fetchBrandSuggestions = (query = {}) =>
  globalGetService('dca/brands/suggestions', {
    ...query,
    limit: 20,
    status: 'ACTIVE',
  }).then((response) => {
    return response?.ok
      ? response.data?.map(
          (b: { brandName: string; brandUid: string }) => b.brandName
        )
      : [];
  });

const FILTER_LOADERS = {
  suggestion: {
    brandName: {
      minLength: 2,
      load: (val: string) => fetchBrandSuggestions({ prefix: val }),
    },
  },
};

type AppliedFilter = { [K in keyof typeof FILTER_CONFIGS]?: string };

const CampaignsByPrimaryDcas = () => {
  const [modalDisabled, setModalDisabled] = useState(false);
  const [modalType, setModalType] = useState<CampaignActionType>(
    CampaignActionType.None
  );
  const [campaignUid, setCampaignUid] = useState('');
  const [isFilterVisible, setIsFilterVisible] = useState(false);
  const toggleFilterVisibility = () => setIsFilterVisible((prev) => !prev);
  const [processingCampaignUid, setProcessingCampaignUid] = useState<
    string | null
  >(null);
  const [rejectionCategories, setRejectionCategories] = useState<SelectItem[]>(
    []
  );
  const [approvedMnoOptions, setApprovedMnoOptions] = useState<SelectItem[]>(
    []
  );

  const {
    appliedFilters,
    additionalFilters,
    candidateFilters,
    configs: filterConfigs,
    handleEdit,
    handleApply,
    setOptions,
    setConfig,
  } = useFilters({
    configs: FILTER_CONFIGS,
    loaders: FILTER_LOADERS,
  });

  const fetchFilterOptions = async () => {
    try {
      const usecaseTypes = await getAllUsecaseTypes();
      const dcas = await fetchAllDcas();
      setOptions(
        'usecase',
        usecaseTypes.map((item: UseCaseType) => ({
          label: item.displayName,
          value: item.id,
        }))
      );
      setOptions(
        'primaryDcaUid',
        dcas.map((dca: Dca) => ({
          label: dca.displayName,
          value: dca.uid,
        }))
      );
    } catch (error) {
      // handle error if needed
    }
  };

  const fetchCampaignsAsSecondaryDca = async (
    filters: Record<string, any> = {}
  ) => {
    const queries = {
      secondarySharingStatus: 'PENDING,ACCEPTED',
      ...filters,
      ...(appliedFilters as AppliedFilter),
      ...additionalFilters,
    };
    const { provisional } = queries;

    if (provisional !== 'true' && provisional !== 'false') {
      return {
        data: table.tableData,
        total: table.total,
        rowsPerPage: table.pagination.rowsPerPage,
        page: table.pagination.page,
      };
    }

    const response = await globalGetService(`dca/campaigns/secondaryDca`, {
      ...queries,
      channelId: '10DLC',
    });

    const { data = {} } = response;
    return {
      data: data.records || [],
      total: data.totalRecords || 0,
      rowsPerPage: data.recordsPerPage || 10,
      page: data.page || 1,
    };
  };

  const fetchRejectionCategories = async (): Promise<void> => {
    const options = await getRejectionCategoryOptions();
    setRejectionCategories(options);
  };

  const fetchApprovedMnoOptions = async () => {
    const options = await getApprovedMnoOptions();
    setApprovedMnoOptions(options);
  };

  const table = useTable<CampaignsByDcaListItem>(fetchCampaignsAsSecondaryDca, {
    cappingTotal: MAX_EVENT_RECORDS,
    deps: [appliedFilters, additionalFilters],
    defaultFilter: {
      sortField: 'sharedOnDate',
      ascendingOrder: false,
    },
  });

  useEffect(() => {
    fetchFilterOptions();
    fetchApprovedMnoOptions();
    CAMPAIGN_REJECTION_ENABLED && fetchRejectionCategories();
  }, []);

  useEffect(() => {
    const newFilters = processFilterSpecialCase(candidateFilters);
    handleEdit(newFilters);
  }, [candidateFilters.status, candidateFilters.secondarySharingStatus]);

  // handle the case when user manually changes the filter value in URL
  useEffect(() => {
    const params = processFilterSpecialCase(appliedFilters);
    if (!isEqual(params, appliedFilters)) {
      handleApply(params);
    }
  }, [appliedFilters.status, appliedFilters.secondarySharingStatus]);

  useEffect(() => {
    const { provisional } = appliedFilters;
    if (provisional !== 'true' && provisional !== 'false') {
      handleApply({
        ...appliedFilters,
        provisional: 'false',
      });
    }
  }, [appliedFilters.provisional]);

  const handleActionClick = (type: CampaignActionType, uid: string) => {
    setModalDisabled(false);
    setModalType(type);
    setCampaignUid(uid);
  };

  const handleModalClose = (reload = false) => {
    setModalDisabled(false);
    setModalType(CampaignActionType.None);
    setCampaignUid('');
    if (reload) {
      table.reload();
    }
  };

  const setColumnLoading = async (handlingCampaignUid: string | null) => {
    setProcessingCampaignUid(handlingCampaignUid);
    await waiting(3000);
  };

  const handleModalSubmit = async (explanation = '') => {
    setModalDisabled(true);
    setColumnLoading(campaignUid);
    try {
      if (modalType === 'accept') {
        await acceptPrimaryDcaCampaign(campaignUid);
      } else {
        await rejectPrimaryDcaCampaign(campaignUid, explanation);
      }
    } catch (e) {
      // handle error if needed
    } finally {
      setProcessingCampaignUid(null);
    }
    handleModalClose();
    table.reload();
  };

  const processFilterSpecialCase = (filterValues = {}) => {
    const isStatusExpired = filterValues.status === 'EXPIRED';
    const newValues = { ...filterValues };
    if (isStatusExpired && filterValues.secondarySharingStatus !== 'ACCEPTED') {
      newValues.secondarySharingStatus = 'ACCEPTED';
    }
    if (
      Boolean(filterConfigs.secondarySharingStatus.disabled) !== isStatusExpired
    ) {
      setConfig('secondarySharingStatus', {
        ...filterConfigs.secondarySharingStatus,
        disabled: isStatusExpired,
      });
    }

    return newValues;
  };

  return (
    <section
      className="campaigns-by-primaryDcas-section"
      data-testid="campaignsByPrimaryDcas"
    >
      <Container
        maxWidth={false}
        className="campaigns-by-primaryDcas-container"
      >
        {table.pageLoading ? (
          <Loader />
        ) : (
          <>
            <Flex
              sx={{
                alignItems: 'center',
                mb: '8px',
                fontWeight: 600,
                fontSize: 'H600',
                lineHeight: '32px',
                color: 't.black80',
              }}
            >
              <span>
                {!table.pageLoading && table.total !== 0
                  ? `${table.total} 10DLC Campaigns Shared by Primary DCAs`
                  : '10DLC Campaigns Shared by Primary DCAs'}
              </span>
              <BoxV2
                sx={{
                  mt: '5px',
                  ml: '12px',
                  fontWeight: 400,
                  fontSize: 'H200',
                  lineHeight: '14px',
                  color: 't.black60',
                }}
              >
                Results capped at first{' '}
                {MAX_EVENT_RECORDS.toLocaleString('en-US')} records
              </BoxV2>
            </Flex>
            <Box className="page-actions">
              <FilterToggleButton
                visible={isFilterVisible}
                onClick={toggleFilterVisibility}
              />
            </Box>
            {isFilterVisible && (
              <TableFilter
                configs={filterConfigs}
                candidateValues={candidateFilters}
                appliedValues={appliedFilters}
                onCandidateValuesChange={handleEdit}
                onAppliedValuesChange={handleApply}
                className="primaryDcas-campaigns"
                data-testid="campaignsByPrimaryDcasTableFilter"
              />
            )}
            <Grid container justify="center" spacing={0}>
              <Table
                testId="campaignByPrimaryDcasTable"
                headRows={headRows}
                emptyState="no campaigns to view"
                loading={table.loading}
                tableData={table.tableData.map((record) => (
                  <CampaignsByPrimaryDcasListingRow
                    key={record.uid}
                    campaign={{
                      ...record,
                      onProcessing: processingCampaignUid === record.uid,
                    }}
                    handleActionClick={handleActionClick}
                  />
                ))}
                handleChangePage={table.handleChangePage}
                createSortHandler={table.handleSorting}
                filter={table.filter}
                pagination={table.pagination}
              />
              {table.total > MAX_EVENT_RECORDS ? (
                <div className="text-center" style={{ paddingTop: '25px' }}>
                  This page can only show the latest {MAX_EVENT_RECORDS} records
                  out of the total of {table.total}. Contact support for details
                  on your remaining records.
                </div>
              ) : null}
            </Grid>
          </>
        )}
      </Container>
      {campaignUid &&
        (CAMPAIGN_REJECTION_ENABLED ? (
          <>
            <AcceptCampaignModal
              open={modalType === CampaignActionType.Accept}
              campaignUid={campaignUid}
              options={approvedMnoOptions}
              onCancel={() => handleModalClose(false)}
              onSubmit={() => handleModalClose(true)}
            />
            <DcaCampaignDeclineModal
              open={modalType === CampaignActionType.Reject}
              campaignUid={campaignUid}
              categories={rejectionCategories}
              onClose={handleModalClose}
            />
          </>
        ) : (
          <CampaignsByPrimaryDcaActionDialog
            open={
              modalType === CampaignActionType.Accept ||
              modalType === CampaignActionType.Reject
            }
            disabled={modalDisabled}
            type={modalType}
            onClose={handleModalClose}
            onSubmit={handleModalSubmit}
          />
        ))}
    </section>
  );
};

export default CampaignsByPrimaryDcas;
